// The country a contact currently lives in, stored as an ISO 3166-1 alpha-2 code ("AE", "EG").
//
// Mirrors backend/src/utils/countries.js — the two lists must hold the same codes, because the
// backend refuses any code not on its own. Names are never stored: `Intl.DisplayNames` translates a
// code into the UI's language, so the same contact reads "Egypt" in English and "مصر" in Arabic.

export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;

/** Where this product's leads actually live. Shown first in every picker. */
export const PINNED_COUNTRY_CODES: readonly string[] = ['AE', 'SA', 'EG', 'KW', 'QA', 'BH', 'OM', 'JO'];

const displayNamesCache = new Map<string, Intl.DisplayNames>();

const displayNamesFor = (locale: string) => {
  let names = displayNamesCache.get(locale);
  if (!names) {
    names = new Intl.DisplayNames([locale], { type: 'region' });
    displayNamesCache.set(locale, names);
  }
  return names;
};

/**
 * The country's name in `lang`. Falls back to English, then to the code, so a bad language tag can
 * never blank a cell out. Accepts Meta's template language form ("en_US") as well as i18next's.
 */
export const countryName = (code: string | null | undefined, lang = 'en'): string => {
  if (!code) return '';
  const upper = code.toUpperCase();
  const locale = (lang || 'en').replace(/_/g, '-');

  for (const candidate of [locale, 'en']) {
    try {
      const name = displayNamesFor(candidate).of(upper);
      if (name) return name;
    } catch {
      // An unparseable language tag. Try the next candidate.
    }
  }
  return upper;
};

export interface CountryOption {
  code: string;
  name: string;
  pinned: boolean;
}

/** Every country, named in `lang`: the pinned ones first in their own order, then alphabetical. */
export const sortedCountries = (lang = 'en'): CountryOption[] => {
  const pinned = PINNED_COUNTRY_CODES.map((code) => ({ code, name: countryName(code, lang), pinned: true }));
  const rest = COUNTRY_CODES
    .filter((code) => !PINNED_COUNTRY_CODES.includes(code))
    .map((code) => ({ code, name: countryName(code, lang), pinned: false }))
    .sort((a, b) => a.name.localeCompare(b.name, lang));
  return [...pinned, ...rest];
};
